import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Observable } from 'rxjs';
import { TrainerFilterStateService } from 'src/app/features/golfer/services/trainer-filter-state.service';
import { NotificationService } from '../notification/notification.service';
import { TrainerStatusService } from 'src/app/core/services/trainer-status.service';
import { UserStateService } from 'src/app/core/services/user-state.service';

export interface LoginResponse {
  success: boolean;
  message: string;
  data: {
    token: string;
    user_name: string;
    user_status: number;
    userData: any;
    userId: number;
    profile_img: string;
    unread_notification_count: number;
  };
}

/** Forgot-password OTP API (POST forgot-password/send-otp). */
export interface ForgotPasswordSendOtpResponse {
  status: boolean;
  code: number;
  msg?: string;
  err?: string;
  data?: { expires_in_minutes?: number };
}

/** Forgot-password reset API (POST forgot-password/reset). */
export interface ForgotPasswordResetResponse {
  status: boolean;
  code: number;
  msg?: string;
  err?: string;
}

/** Registration — send OTP (POST register/send-otp). */
export interface RegisterSendOtpResponse {
  success: boolean;
  message?: string;
  data?: { expires_in_minutes?: number } | null;
}

/** Registration — complete (POST register). `message` may be a JSON string of validation errors. */
export interface RegisterCompleteResponse {
  success: boolean;
  message?: string;
  err?: string;
  email?: string;
  msg?: string;
  code?: number;
  role?: string[];
  name?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  baseUrl = environment.apiUrl;

  constructor(
    private http: HttpClient,
    private filterStateService: TrainerFilterStateService,
    private notificationService: NotificationService,
    private trainerStatusService: TrainerStatusService,
    private userState: UserStateService
  ) { }

  login(data: { email: string; password: string; deviceToken: string; remember: boolean }): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.baseUrl}/login`, data);
  }

  logout() {
    this.notificationService.teardownForLogout();
    this.trainerStatusService.resetStatus();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    this.filterStateService.clearFilterState();
  }

  /**
   * Called when the API returns 401 while a token is still stored (revoked token,
   * password reset on another device, etc.). Clears the same client state as a full
   * sign-out without calling the backend logout endpoint.
   */
  invalidateSessionDueToUnauthorized(): void {
    this.logout();
    localStorage.removeItem('user_id');
    localStorage.removeItem('user_name');
    localStorage.removeItem('email');
    localStorage.removeItem('remember');
    localStorage.removeItem('login_type');
    localStorage.removeItem('user_role');
    localStorage.removeItem('user_type');
    localStorage.removeItem('firebase_uid');
    localStorage.removeItem('activitySearchPayload');
    this.userState.refreshFromStorage();
  }

  isLoggedIn(): boolean {
    return !!this.getToken();
  }

  getToken(): string | null {
    return localStorage.getItem('token');
  }

  getUser(): any {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  }

  register(data: any): Observable<RegisterCompleteResponse> {
    return this.http.post<RegisterCompleteResponse>(`${this.baseUrl}/register`, data);
  }

  /** Request 6-digit email OTP before POST /register (throttled server-side). */
  registerSendOtp(payload: { email: string }): Observable<RegisterSendOtpResponse> {
    return this.http.post<RegisterSendOtpResponse>(`${this.baseUrl}/register/send-otp`, payload);
  }

  /** Step 1: request 6-digit OTP to the email (throttled server-side). */
  forgotPasswordSendOtp(payload: { email: string }): Observable<ForgotPasswordSendOtpResponse> {
    return this.http.post<ForgotPasswordSendOtpResponse>(`${this.baseUrl}/forgot-password/send-otp`, payload);
  }

  /** Step 2: verify OTP and set a new password. */
  forgotPasswordReset(payload: { email: string; otp: string; password: string; c_password: string }): Observable<ForgotPasswordResetResponse> {
    return this.http.post<ForgotPasswordResetResponse>(`${this.baseUrl}/forgot-password/reset`, payload);
  }

  socialLogin(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/social_login`, payload);
  }

  signInWithGoogle(): Observable<any> {
    return this.http.post(`${this.baseUrl}/google-login`, {});
  }

  signInWithApple(): Observable<any> {
    return this.http.post(`${this.baseUrl}/apple-login`, {});
  }
}
